import React from 'react';
import axios from 'axios';
import { useDashboard } from '../contexts/DashboardContext';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const AdminPage = () => {
  const {
    API, activeTab, adminData, businessToDelete, deletingBusiness, isMasterAdmin,
    originalAdminToken, packages, products, setAdminData, setBusinessToDelete, setDeletingBusiness,
    setShowDeleteBusinessModal, showDeleteBusinessModal, showToast, user,
  } = useDashboard();

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-6 md:px-8 lg:px-12">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Admin Panel</h2>
          <p className="text-gray-500">Manage all businesses and users</p>
        </div>
        <span className="px-3 py-1 bg-red-100 text-red-700 text-sm font-medium rounded-full">Master Admin</span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{adminData.businesses.length}</p>
              <p className="text-sm text-gray-500">Businesses</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{adminData.users.length}</p>
              <p className="text-sm text-gray-500">Total Users</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{adminData.businesses.reduce((sum, b) => sum + (b.appointment_count || 0), 0)}</p>
              <p className="text-sm text-gray-500">Total Appointments</p>
            </div>
          </div>
        </div>
      </div>

      {/* Businesses List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold">All Businesses</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {adminData.businesses.map((business) => (
            <div key={business.id} className="p-4 hover:bg-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600 font-semibold">
                  {business.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{business.name}</h4>
                  <p className="text-sm text-gray-500">{business.owner_email}</p>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{business.user_count} users</p>
                  <p className="text-xs text-gray-500">{business.appointment_count} appointments</p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      // Find owner user for this business
                      const ownerUser = adminData.users.find(u => u.business_id === business.id && u.role === 'owner');
                      if (!ownerUser) {
                        showToast('No owner found for this business', 'error');
                        return;
                      }
                      
                      const token = localStorage.getItem('token');
                      localStorage.setItem('originalAdminToken', token);
                      
                      const response = await axios.post(`${API}/admin/impersonate/${ownerUser.id}`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      
                      localStorage.setItem('token', response.data.access_token);
                      localStorage.setItem('user', JSON.stringify({
                        user_id: response.data.user_id,
                        business_id: response.data.business_id,
                        business_name: response.data.business_name,
                        user_name: response.data.user_name
                      }));
                      
                      showToast(`Now viewing as ${response.data.user_name}`, 'success');
                      
                      // Reload page after short delay - go to /app to stay in dashboard
                      setTimeout(() => {
                        window.location.href = '/app';
                      }, 500);
                    } catch (err) {
                      console.error('Impersonation error:', err);
                      showToast('Failed to impersonate user: ' + (err.response?.data?.detail || err.message), 'error');
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  data-testid={`view-as-btn-${business.id}`}
                >
                  View As
                </button>
                <button
                  onClick={() => {
                    setBusinessToDelete(business);
                    setShowDeleteBusinessModal(true);
                  }}
                  className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                  data-testid={`delete-business-btn-${business.id}`}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
          {adminData.businesses.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <p>Loading businesses...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
